import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import Link from 'next/link'
import { getVisiblePages } from './helper'

export const Pagination = ({
  totalCount,
  perPage,
  page,
  linkGenerator,
  hasPreviousPage,
  hasNextPage,
}: PaginationProps) => {
  const hasTotalCount = typeof totalCount === 'number'
  const totalPages = hasTotalCount ? Math.ceil(totalCount / (perPage || 10)) : 0

  const pages = getVisiblePages(totalPages, page)

  if (pages.length <= 1 && !hasPreviousPage && !hasNextPage) return <></>

  return (
    <>
      <div className="flex items-center justify-center bg-gray-50 md:bg-white p-2">
        <div className="flex flex-1 justify-between md:hidden">
          <div>
            {hasPreviousPage && (
              <Link
                href={linkGenerator(page - 1)}
                prefetch={false}
                className={`inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-lg text-gray-700 hover:bg-gray-50`}
              >
                <ChevronLeftIcon className="size-5 mr-1" />
                Previous
              </Link>
            )}
          </div>
          <div>
            {hasNextPage && (
              <Link
                href={linkGenerator(page + 1)}
                prefetch={false}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-lg text-gray-700 hover:bg-gray-50"
              >
                Next
                <ChevronRightIcon className="size-5 ml-1" />
              </Link>
            )}
          </div>
        </div>
      </div>
      {/* Desktop view */}
      <div>
        <div className="hidden md:inline-block">
          <div>
            <nav
              aria-label="Pagination"
              className="ring-1 ring-inset  ring-gray-300 inline-flex -space-x-px rounded-md overflow-hidden shadow-xs"
            >
              {hasPreviousPage && (
                <Link
                  href={linkGenerator(page - 1)}
                  prefetch={false}
                  className={`rounded-l-md inline-flex items-center px-2 py-2 text-lg text-vi-red ring-1 ring-inset ring-gray-300 hover:bg-gray-50`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon aria-hidden="true" className="size-5" />
                </Link>
              )}

              {hasTotalCount ? (
                pages.map((item, index) =>
                  item === '...' ? (
                    <span
                      key={`${index}-dots`}
                      className="inline-flex items-center px-4 py-2 font-semibold text-lg text-gray-700 ring-1 ring-inset ring-gray-300"
                    >
                      ...
                    </span>
                  ) : (
                    <Link
                      key={`${index}-${item}`}
                      href={linkGenerator(Number(item))}
                      prefetch={false}
                      aria-current={item === page ? 'page' : undefined}
                      className={`inline-flex items-center px-4 py-2 font-semibold text-lg ring-1 ring-inset ${
                        item === page
                          ? 'bg-vi-red text-white ring-vi-red'
                          : 'text-vi-red hover:bg-gray-50 cursor-pointer ring-gray-300'
                      }`}
                    >
                      {item}
                    </Link>
                  )
                )
              ) : (
                <span
                  aria-current="page"
                  className="inline-flex items-center px-4 py-2 font-semibold text-lg bg-vi-red text-white ring-1 ring-inset ring-vi-red"
                >
                  {page}
                </span>
              )}

              {hasNextPage && (
                <Link
                  href={linkGenerator(page + 1)}
                  prefetch={false}
                  className={`rounded-r-md inline-flex items-center px-2 py-2 text-lg text-vi-red ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon aria-hidden="true" className="size-5" />
                </Link>
              )}
            </nav>
          </div>
        </div>
      </div>
    </>
  )
}

interface PaginationProps {
  totalCount?: number
  perPage?: number
  page: number
  hasPreviousPage?: boolean
  hasNextPage?: boolean
  linkGenerator: (page: number) => string
  scrollToId?: string
  onPageChange?: (page: number) => void
}
